import { CommonModule } from '@angular/common';
import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import { IConstructable } from '@mosaik/core';
import { FileService } from '@mosaik/presentation';
import { BytesPipeModule } from '../../../../Pipes/BytesPipe';
import { IUploadService } from '../../../Services/Interfaces/IUploadService';
import { EmptyUploadStrategy } from '../../../Services/Providers/EmptyUploadStrategy';
import { IUploadStrategy } from '../../../Services/Providers/Interfaces/IUploadStrategy';
import { UploadService } from '../../../Services/UploadService';
import { GroupBoxModule } from '../../Grouping/GroupBox/Module';
import { ProgressBarModule } from '../../Ranges/ProgressBar/Module';
import { ListModule } from '../../Selectors/List/Module';
import { UploadComponent } from './Upload';
import { UploadPickerComponent } from './UploadPicker';

/**
 * @public
 */
export const UPLOAD_MODULE_CONFIG = new InjectionToken<IUploadModuleConfig>('Upload Module Configuration');

/**
 * @internal
 * @hidden
 * @factory
 */
export function uploadServiceFactory(config: IUploadModuleConfig, fileService: FileService): IUploadService {
  const empty = { name: 'empty', instance: new EmptyUploadStrategy() };
  const strategies = config.strategies
    ? config.strategies.map(x => ({ name: x.name, instance: new x.type(fileService) }))
    : [empty];
  const service = new UploadService();
  strategies.forEach(x => service.register(x.name, x.instance));
  return service;
}

/**
 * @public
 */
@NgModule({
  declarations: [UploadComponent, UploadPickerComponent],
  exports: [UploadComponent, UploadPickerComponent],
  imports: [CommonModule, GroupBoxModule, ListModule, ProgressBarModule, BytesPipeModule]
})
export class UploadModule {
  public static forRoot(config: Partial<IUploadModuleConfig>): ModuleWithProviders<UploadModule> {
    return {
      ngModule: UploadModule,
      providers: [
        { provide: UPLOAD_MODULE_CONFIG, useValue: config },
        { provide: UploadService, useFactory: uploadServiceFactory, deps: [UPLOAD_MODULE_CONFIG, FileService] }
      ]
    };
  }
}

/**
 * @public
 */
export interface IUploadModuleConfig {
  strategies: Array<{ name: string; type: IConstructable<IUploadStrategy> }>;
}
